import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _articleSearchComponentQuery from "./__generated__/articleSearchComponentQuery.graphql";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bind } from 'bind-event-listener';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Popup } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { useProductDependencies } from '@atlassian/company-hub-common';
import { KeyboardHighlightProvider } from '@atlassian/search-dialog';
import { useArticleSearch } from '../../controllers/use-article-search';
import { SearchDropdown } from './search-dropdown';
import { SearchTextField } from './search-textfield';
export var ArticleSearchComponent = function ArticleSearchComponent(_ref) {
  var _inputBoxRef$current;
  var isEditor = _ref.isEditor,
    placeholderText = _ref.placeholderText;
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    queryWord = _useState2[0],
    setQueryWord = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    showDropdown = _useState4[0],
    setShowDropdown = _useState4[1];
  var inputBoxRef = useRef(null);
  var inputRef = useRef(null);
  var _useState5 = useState((_inputBoxRef$current = inputBoxRef.current) === null || _inputBoxRef$current === void 0 ? void 0 : _inputBoxRef$current.offsetWidth),
    _useState6 = _slicedToArray(_useState5, 2),
    _ = _useState6[0],
    setInputWidth = _useState6[1];
  var _useProductDependenci = useProductDependencies(),
    cloudId = _useProductDependenci.cloudId,
    helpCenterAri = _useProductDependenci.helpCenterAri;

  /* eslint-disable @atlassian/relay/unused-fields */
  var searchResponse = useLazyLoadQuery(_articleSearchComponentQuery, {
    cloudId: cloudId,
    helpCenterAri: helpCenterAri !== null && helpCenterAri !== void 0 ? helpCenterAri : '',
    input: {
      query: queryWord,
      contentTypes: ['FOLDER', 'PAGE']
    },
    first: 10
  });
  /* eslint-enable @atlassian/relay/unused-fields */

  var entities = useMemo(function () {
    var _searchResponse$helpO;
    return (searchResponse === null || searchResponse === void 0 || (_searchResponse$helpO = searchResponse.helpObjectStore_searchArticlesForSupportSite) === null || _searchResponse$helpO === void 0 ? void 0 : _searchResponse$helpO.edges) || [];
  }, [searchResponse === null || searchResponse === void 0 ? void 0 : searchResponse.helpObjectStore_searchArticlesForSupportSite]);
  var hasQueryError = Boolean(entities.find(function (edge) {
    var _edge$node;
    return edge === null || edge === void 0 || (_edge$node = edge.node) === null || _edge$node === void 0 ? void 0 : _edge$node.message;
  }));
  var _useArticleSearch = useArticleSearch(searchResponse),
    handleSearch = _useArticleSearch.handleSearch,
    clearSearch = _useArticleSearch.clearSearch,
    isLoading = _useArticleSearch.isLoading,
    data = _useArticleSearch.data;
  useEffect(function () {
    var listener = function listener() {
      var _inputBoxRef$current2;
      return setInputWidth((_inputBoxRef$current2 = inputBoxRef.current) === null || _inputBoxRef$current2 === void 0 ? void 0 : _inputBoxRef$current2.offsetWidth);
    };
    var unbind = bind(window, {
      type: 'resize',
      listener: listener
    });
    return unbind;
  }, []);
  var closeDropdown = function closeDropdown() {
    if (showDropdown) {
      setShowDropdown(false);
    }
  };
  var trigger = useCallback(function (triggerProps) {
    var handleInput = function handleInput(searchInput) {
      clearSearch();
      setQueryWord(searchInput);
      handleSearch(searchInput);
      setShowDropdown(searchInput.length > 0);
    };
    return /*#__PURE__*/React.createElement(Box, {
      ref: inputBoxRef
    }, /*#__PURE__*/React.createElement(SearchTextField, _extends({
      keyboardRef: inputRef
    }, triggerProps, {
      ref: triggerProps.ref,
      onInput: handleInput,
      isLoading: isLoading,
      isReadOnly: isEditor,
      placeholderText: placeholderText,
      showDropdown: showDropdown,
      queryWord: queryWord,
      setQueryWord: setQueryWord
    })));
  }, [clearSearch, handleSearch, isEditor, isLoading, placeholderText, showDropdown, queryWord]);
  var isExpanded = isLoading || hasQueryError || Boolean(data);
  return /*#__PURE__*/React.createElement(Popup, {
    shouldReturnFocus: false,
    content: function content(contentProps) {
      var _inputBoxRef$current3;
      return !isExpanded ? /*#__PURE__*/React.createElement(Box
      /* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */, {
        style: {
          display: 'none'
        }
        /* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */
      }) : /*#__PURE__*/React.createElement(KeyboardHighlightProvider, {
        listenerNode: inputRef === null || inputRef === void 0 ? void 0 : inputRef.current
      }, /*#__PURE__*/React.createElement(SearchDropdown, _extends({}, contentProps, {
        inputWidth: ((_inputBoxRef$current3 = inputBoxRef.current) === null || _inputBoxRef$current3 === void 0 ? void 0 : _inputBoxRef$current3.offsetWidth) || 0,
        data: data,
        isLoading: isLoading,
        hasQueryError: hasQueryError
      })));
    },
    isOpen: showDropdown,
    autoFocus: false,
    placement: "bottom-start",
    trigger: trigger,
    onClose: closeDropdown
  });
};